import { ReactNode, useEffect, useMemo, useState } from "react";
import {
  MsalAuthenticationTemplate,
  useIsAuthenticated,
} from "@azure/msal-react";
import { InteractionType, RedirectRequest } from "@azure/msal-browser";
import { Navigate } from "react-router-dom";
import { useAppDispatch } from "../app/hooks";
import { getCurrentUser, type MSGraphUser } from "../app/msgraphapi/graphService";
import { getAccount } from "../app/accountService";
import { loginRequest } from "../app/auth/authConfig";
import {
  setAppUserAccount,
  setIsImpersonating,
  setMsGraphUser,
  setProfilePicture,
  setUserDesignation,
  setUserRoles,
} from "../app/auth/createAuthSlice";

type ProtectedRouteShellProps = {
  children: ReactNode;
};

function ProtectedRouteInitializer({ children }: ProtectedRouteShellProps) {
  const isAuthenticated = useIsAuthenticated();
  const appDispatch = useAppDispatch();
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [hasBootstrapFailed, setHasBootstrapFailed] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const applyAccountData = (
      user: MSGraphUser,
      pictureUrl: string | undefined,
      account: Awaited<ReturnType<typeof getAccount>>
    ) => {
      appDispatch(setMsGraphUser(user));
      appDispatch(setProfilePicture(pictureUrl));
      appDispatch(setUserDesignation(user?.jobTitle));
      appDispatch(setAppUserAccount(account));
      appDispatch(
        setUserRoles(account.data?.permissions?.roles || account.permissions?.roles)
      );
      if (localStorage.getItem("impersonate")) {
        appDispatch(setIsImpersonating(true));
      }
    };

    const initializeUserContext = async () => {
      if (!isAuthenticated) {
        setIsLoadingProfile(false);
        return;
      }

      try {
        const { user, pictureUrl } = await getCurrentUser();

        try {
          const account = await getAccount();
          if (!isMounted) {
            return;
          }
          applyAccountData(user, pictureUrl, account);
        } catch (error) {
          if (localStorage.getItem("impersonate")) {
            localStorage.removeItem("impersonate");

            try {
              const fallbackAccount = await getAccount();
              if (!isMounted) {
                return;
              }
              applyAccountData(user, pictureUrl, fallbackAccount);
            } catch (fallbackError) {
              console.error("Error fetching fallback account", fallbackError);
              if (isMounted) {
                setHasBootstrapFailed(true);
              }
            }
          } else {
            console.error("Error fetching account", error);
            if (isMounted) {
              setHasBootstrapFailed(true);
            }
          }
        }
      } catch (error) {
        if (isMounted) {
          setHasBootstrapFailed(true);
        }
      } finally {
        if (isMounted) {
          setIsLoadingProfile(false);
        }
      }
    };

    initializeUserContext();

    return () => {
      isMounted = false;
    };
  }, [appDispatch, isAuthenticated]);

  if (hasBootstrapFailed) {
    return <Navigate to="/" replace />;
  }

  if (isLoadingProfile) {
    return null;
  }

  return <>{children}</>;
}

export default function ProtectedRouteShell({ children }: ProtectedRouteShellProps) {
  const authRequest: RedirectRequest = useMemo(
    () => ({
      scopes: loginRequest.scopes,
    }),
    []
  );

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={authRequest}
    >
      <ProtectedRouteInitializer>{children}</ProtectedRouteInitializer>
    </MsalAuthenticationTemplate>
  );
}
